import { forwardRef } from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cn } from '@/utils/cn';

/**
 * Radio Component
 *
 * Radio input using Radix UI RadioGroup
 * Styled to match the Checkbox component
 */

interface RadioGroupProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  name?: string;
  className?: string;
  children: React.ReactNode;
  orientation?: 'horizontal' | 'vertical';
}

interface RadioItemProps {
  value: string;
  label?: string;
  description?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

/**
 * RadioGroup - Container for radio items
 */
export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      value,
      defaultValue,
      onValueChange,
      disabled,
      name,
      className,
      children,
      orientation = 'vertical',
    },
    ref
  ) => {
    return (
      <RadioGroupPrimitive.Root
        ref={ref}
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        disabled={disabled}
        name={name}
        orientation={orientation}
        className={cn(
          'flex',
          orientation === 'vertical' ? 'flex-col gap-3' : 'flex-row gap-4',
          className
        )}
      >
        {children}
      </RadioGroupPrimitive.Root>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';

/**
 * RadioItem - Individual radio option
 */
export const RadioItem = forwardRef<HTMLButtonElement, RadioItemProps>(
  ({ value, label, description, disabled, className, id }, ref) => {
    const itemId = id || `radio-${value}`;

    return (
      <div className={cn('flex items-start gap-2', className)}>
        <RadioGroupPrimitive.Item
          ref={ref}
          id={itemId}
          value={value}
          disabled={disabled}
          className={cn(
            'w-5 h-5 mt-0.5',
            'border-2 border-gray-300 rounded-full',
            'focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2',
            'transition-all duration-200',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'data-[state=checked]:border-primary-500',
            'flex items-center justify-center',
            'bg-white'
          )}
        >
          <RadioGroupPrimitive.Indicator
            className={cn(
              'w-2.5 h-2.5 rounded-full bg-primary-500',
              'data-[state=checked]:animate-in data-[state=checked]:zoom-in-50'
            )}
          />
        </RadioGroupPrimitive.Item>

        {(label || description) && (
          <label
            htmlFor={itemId}
            className={cn(
              'cursor-pointer select-none',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {label && (
              <span className="block text-sm font-medium text-gray-700">
                {label}
              </span>
            )}
            {description && (
              <span className="block text-xs text-gray-500 mt-0.5">
                {description}
              </span>
            )}
          </label>
        )}
      </div>
    );
  }
);

RadioItem.displayName = 'RadioItem';

/**
 * RadioCard - Radio option styled as a selectable card
 */
interface RadioCardProps extends RadioItemProps {
  children?: React.ReactNode;
}

export const RadioCard = forwardRef<HTMLButtonElement, RadioCardProps>(
  ({ value, label, description, disabled, className, id, children }, ref) => {
    const itemId = id || `radio-card-${value}`;

    return (
      <label
        htmlFor={itemId}
        className={cn(
          'flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all',
          'border-gray-200 bg-white hover:border-gray-300',
          'has-[[data-state=checked]]:border-bs-secondary has-[[data-state=checked]]:bg-soft-mint',
          disabled && 'opacity-50 cursor-not-allowed hover:border-gray-200',
          className
        )}
      >
        <RadioGroupPrimitive.Item
          ref={ref}
          id={itemId}
          value={value}
          disabled={disabled}
          className={cn(
            'w-5 h-5 mt-0.5 flex-shrink-0',
            'border-2 border-gray-300 rounded-full',
            'focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2',
            'transition-all duration-200',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'data-[state=checked]:border-primary-500',
            'flex items-center justify-center',
            'bg-white'
          )}
        >
          <RadioGroupPrimitive.Indicator
            className={cn(
              'w-2.5 h-2.5 rounded-full bg-primary-500',
              'data-[state=checked]:animate-in data-[state=checked]:zoom-in-50'
            )}
          />
        </RadioGroupPrimitive.Item>

        <div className="flex-1">
          {label && (
            <span className="block text-sm font-medium text-gray-700">
              {label}
            </span>
          )}
          {description && (
            <span className="block text-xs text-gray-500 mt-0.5">
              {description}
            </span>
          )}
          {children}
        </div>
      </label>
    );
  }
);

RadioCard.displayName = 'RadioCard';
